import { z } from 'zod'
import { AdviesId, Block, CriterionId, DossierId, EventId, FieldId, InstantieId, InvitationId, ReactieId, RevisieId, SectionId, ToekenningId } from './id.js'
import { Role, Side } from './identity.js'
import { BelangPartijen } from './belangenverklaring.js'
import { Moment, Person, compareMoments } from './moment.js'

/**
 * Advies vragen in het formulier (R-184, besluit van 30 oktober 2026, ontwerp 7.4 en 7.4a).
 *
 * Een medewerker aan de interne kant met een toekenning van het processysteem (de toetser, of een
 * invuller van NWO) vraagt iemand om advies over het formulier, een sectie of een vraag. Dat is een
 * deling met het recht "Advies geven": de adviseur krijgt een uitnodiging op zijn e-mailadres, en na
 * verzilveren een toekenning met bron deling en lezen op de gedeelde secties. Naast die deling staat
 * dit record, het adviesverzoek, met het gesprek erin.
 *
 * **Een eigen record en geen veld op de uitnodiging of de toekenning.** `Invitation` en `Toekenning`
 * zijn strikt, en een pod van vóór dit besluit parset ze strikt; een veld erbij laat hem omvallen
 * (D-36). Het verband staat daarom alleen hier, in `uitnodigingId` en `toekenningId`. Een oude pod die
 * de toekenning ziet, geeft lezen op de gedeelde secties en verder niets, en zelfs dat pas na de
 * belangenverklaring, want tot dan staat de toekenning op uitgenodigd.
 *
 * **Het gesprek staat niet in het sectiedocument.** Het sectiedocument van de invoerlaag wordt ook
 * door de externe kant gesynchroniseerd, en een adviseur leest en schrijft daar niet. De afscherming
 * volgt zo uit de opslag en niet uit de weergave: de externe kant krijgt geen adviesverzoek, op geen
 * enkele route.
 *
 * **De belangenverklaring komt eerst** (besluit van NWO, 30 oktober 2026). Een adviseur ziet niets van
 * wat er is gedeeld voordat hij verklaart geen belang te hebben. Verklaart hij een belang, dan eindigt
 * het verzoek. De verklaring staat op het verzoek en gaat mee in elk bericht, met de partijen die de
 * adviseur zag (R-184j).
 *
 * **NWO heeft het laatste woord** (besluit van de gebruiker van 31 oktober 2026, R-184k). Een medewerker
 * aan de interne kant met een toekenning van het processysteem, of functioneel beheer in een opening,
 * kan de verklaring beoordelen: geen belang of wel belang, altijd met een toelichting. Het laatste
 * oordeel geldt boven de verklaring (`belangUitkomst`). Wel belang beëindigt het verzoek; geen belang na
 * een gemeld belang laat het weer lopen, en dat is de ene weg terug uit een einde. De verklaring zelf
 * blijft staan zoals de adviseur haar gaf, en elk oordeel staat erbij, oudste eerst.
 */

/** Waar de vraag over gaat: het hele formulier, een sectie, een vraag of een toetscriterium. */
export const AdviesAnkerSoort = z.enum(['formulier', 'sectie', 'veld', 'criterium'])
export type AdviesAnkerSoort = z.infer<typeof AdviesAnkerSoort>

/**
 * Het anker van een adviesverzoek.
 *
 * Een criterium is een veld in een toetssectie (control `criterium`), dus een criterium-anker noemt
 * de sectie, het veld en het criterium. Het label is de tekst van het anker zoals de definitie hem
 * noemt, in het Nederlands; de opslagdienst zet het uit de bevroren definitie, zodat het processysteem
 * het verzoek kan tonen zonder de definitie te kennen. `revisieId` is de stand waarop de vraag rust:
 * in de toetsbeurt de revisie die de toetser voor zich heeft, in de invoerbeurt nog geen.
 */
export const AdviesAnker = z
  .strictObject({
    soort: AdviesAnkerSoort,
    sectieId: SectionId.nullable(),
    veldId: FieldId.nullable(),
    criteriumId: CriterionId.nullable(),
    label: z.string().min(1),
    revisieId: RevisieId.nullable(),
  })
  .superRefine((anker, ctx) => {
    const needs = {
      formulier: { sectieId: false, veldId: false, criteriumId: false },
      sectie: { sectieId: true, veldId: false, criteriumId: false },
      veld: { sectieId: true, veldId: true, criteriumId: false },
      criterium: { sectieId: true, veldId: true, criteriumId: true },
    }[anker.soort]
    for (const [field, required] of Object.entries(needs)) {
      const present = anker[field as keyof typeof needs] !== null
      if (present !== required) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `een anker van soort ${anker.soort} ${required ? 'noemt' : 'noemt geen'} ${field}`,
        })
      }
    }
  })
export type AdviesAnker = z.infer<typeof AdviesAnker>

/**
 * De toestand van een adviesverzoek.
 *
 * `gevraagd` bij aanmaken en na een vervolgvraag; `beantwoord` als de adviseur heeft geantwoord;
 * daarna een van vijf eindes. Afgerond en ingetrokken komen van de interne kant, belang-gemeld van
 * de adviseur of van het oordeel van NWO, en vervallen van de omstandigheden: de beurt sloot, de
 * uitnodiging verliep of werd geweigerd, of de deler verloor zijn toegang (R-65e). Een einde is een
 * einde, met één uitzondering: belang-gemeld gaat terug naar gevraagd of beantwoord als NWO oordeelt
 * dat er geen belang is (R-184k). Het gesprek blijft altijd leesbaar.
 */
export const AdviesToestand = z.enum(['gevraagd', 'beantwoord', 'afgerond', 'ingetrokken', 'vervallen', 'belang-gemeld'])
export type AdviesToestand = z.infer<typeof AdviesToestand>

/** De toestanden waarin het gesprek nog loopt en de adviseur nog toegang kan hebben. */
export const OPEN_ADVIES: readonly AdviesToestand[] = ['gevraagd', 'beantwoord']

export function isOpenAdvies(toestand: AdviesToestand): boolean {
  return OPEN_ADVIES.includes(toestand)
}

/**
 * Waarom een verzoek eindigde. Bij vervallen zegt dit welke omstandigheid; bij de andere drie
 * eindes is het dezelfde naam als de toestand, zodat de reden altijd te lezen is zonder de toestand.
 */
export const AdviesEindreden = z.enum([
  'afgerond',
  'ingetrokken',
  'belang-gemeld',
  'beurt-gesloten',
  'uitnodiging-verlopen',
  'uitnodiging-geweigerd',
  'uitnodiging-ingetrokken',
  'toegang-deler-vervallen',
])
export type AdviesEindreden = z.infer<typeof AdviesEindreden>

/**
 * De belangenverklaring van de adviseur (besluit van NWO, 30 oktober 2026).
 *
 * `geen-belang` laat de adviseur verder: pas dan geeft zijn toekenning lezen. `belang` beëindigt het
 * verzoek; de toelichting is dan optioneel en gaat mee naar het processysteem, dat de vrager
 * inlicht.
 *
 * `partijen` is wat de adviseur zag toen hij verklaarde (R-184h, R-184j): de pod rekent het op dat
 * moment uit de stand en het dossier, nooit uit wat de browser stuurt. Null als het formulier de
 * verklaring zonder partijen gebruikt, en bij een verklaring van vóór dit besluit; die leest daarom
 * zonder het veld en krijgt null.
 */
export const Belangenverklaring = z
  .strictObject({
    uitkomst: z.enum(['geen-belang', 'belang']),
    toelichting: z.string().trim().min(1).max(2000).nullable(),
    persoon: Person,
    moment: Moment,
    partijen: BelangPartijen.nullable().default(null),
  })
  .refine((verklaring) => verklaring.uitkomst === 'belang' || verklaring.toelichting === null, {
    path: ['toelichting'],
    message: 'een toelichting hoort bij het melden van een belang',
  })
export type Belangenverklaring = z.infer<typeof Belangenverklaring>

/** De lengte van een toelichting bij een verklaring of een oordeel. */
export const BELANG_TOELICHTING_MAX = 2000

/**
 * Het oordeel van NWO over een belangenverklaring (besluit van de gebruiker van 31 oktober 2026,
 * R-184k).
 *
 * Altijd met een toelichting: wie de adviseur tegenspreekt of bevestigt, zegt waarom, want het gaat
 * naar het processysteem en de adviseur leest het. `rol` zegt langs welke weg: `vrager` is elke
 * interne deelnemer met een toekenning van het processysteem op het dossier en blok, zoals bij een
 * reactie; `beheer` is functioneel beheer in een opening door beheer (R-155f).
 */
export const BelangOordeel = z.strictObject({
  uitkomst: z.enum(['geen-belang', 'belang']),
  toelichting: z.string().trim().min(1).max(BELANG_TOELICHTING_MAX),
  persoon: Person,
  rol: z.enum(['vrager', 'beheer']),
  moment: Moment,
})
export type BelangOordeel = z.infer<typeof BelangOordeel>

/** De lengte van een vraag of antwoord. Ruim voor een adviestekst, en een grens tegen een document vol plakwerk. */
export const ADVIES_TEKST_MAX = 10_000

/**
 * Eén reactie in het gesprek: de vraag, een antwoord of een vervolgvraag, oudste eerst, zoals een
 * bericht in een opmerkingdraad (`thread.ts`). Wie schreef staat als persoon met vaste sleutel en als
 * rol in het gesprek: vrager is elke interne deelnemer met een toekenning van het processysteem.
 */
export const AdviesReactie = z.strictObject({
  id: ReactieId,
  persoon: Person,
  rol: z.enum(['vrager', 'adviseur']),
  tekst: z.string().trim().min(1).max(ADVIES_TEKST_MAX),
  moment: Moment,
})
export type AdviesReactie = z.infer<typeof AdviesReactie>

/** Een datum zonder tijd, zoals `2026-12-12`: de termijn waarvoor de vrager graag antwoord heeft. */
export const AdviesTermijn = z.iso.date()

/**
 * Een adviesverzoek met zijn hele gesprek.
 *
 * `volgnummer` telt elke verandering, te beginnen bij 1; elk bericht aan het processysteem draagt de
 * hele toestand met dit nummer, zodat de ontvanger per verzoek de toestand met het hoogste nummer
 * bewaart en een herhaald of laat bericht geen schade doet.
 */
export const AdviesVerzoek = z
  .strictObject({
    id: AdviesId,
    dossierId: DossierId,
    blok: Block,
    /** De instantie waarop is gevraagd; de adviseur leest alleen deze (ontwerp 7.4a). */
    instantieId: InstantieId,
    anker: AdviesAnker,
    /** Wie vroeg, als ingang met vaste sleutel (R-155e). */
    vrager: Person,
    /** In welke rol de vrager vroeg: als toetser of als invuller aan de interne kant. */
    vragerRol: Role,
    /** Binnen of buiten NWO, zoals de vrager koos; bij verzilveren afgedwongen op de kant van de ingang. */
    adviseurKant: Side,
    uitnodigingId: InvitationId,
    /** De toekenning die bij verzilveren ontstond; null tot dan. */
    toekenningId: ToekenningId.nullable(),
    /** Wie verzilverde; null tot dan. */
    adviseur: Person.nullable(),
    /** De secties die de adviseur mag lezen, altijd alleen lezen. */
    gedeeld: z.array(SectionId).min(1),
    termijn: AdviesTermijn.nullable(),
    toestand: AdviesToestand,
    belangenverklaring: Belangenverklaring.nullable(),
    /**
     * De oordelen van NWO over de verklaring, oudste eerst; het laatste geldt (R-184k). Een verzoek van
     * vóór dit besluit leest als zonder oordelen.
     */
    nwoOordelen: z.array(BelangOordeel).default([]),
    reacties: z.array(AdviesReactie).min(1),
    volgnummer: z.number().int().min(1),
    aangemaaktOp: Moment,
    gewijzigdOp: Moment,
    eindreden: AdviesEindreden.nullable(),
  })
  .superRefine((verzoek, ctx) => {
    if (new Set(verzoek.gedeeld).size !== verzoek.gedeeld.length) {
      ctx.addIssue({ code: 'custom', path: ['gedeeld'], message: 'een sectie staat één keer in wat er is gedeeld' })
    }
    if ((verzoek.adviseur === null) !== (verzoek.toekenningId === null)) {
      ctx.addIssue({ code: 'custom', path: ['adviseur'], message: 'een adviseur en zijn toekenning komen samen, bij verzilveren' })
    }
    if (verzoek.adviseur !== null && verzoek.adviseur === verzoek.vrager) {
      ctx.addIssue({ code: 'custom', path: ['adviseur'], message: 'wie vraagt geeft zichzelf geen advies' })
    }
    if (verzoek.belangenverklaring !== null && verzoek.belangenverklaring.persoon !== verzoek.adviseur) {
      ctx.addIssue({ code: 'custom', path: ['belangenverklaring'], message: 'de belangenverklaring is van de adviseur' })
    }
    const open = isOpenAdvies(verzoek.toestand)
    if (open !== (verzoek.eindreden === null)) {
      ctx.addIssue({ code: 'custom', path: ['eindreden'], message: 'een verzoek draagt een eindreden precies als het is geëindigd' })
    }
    // Wat geldt: het laatste oordeel van NWO, anders de verklaring (R-184k).
    const uitkomst = belangUitkomst(verzoek)
    if (verzoek.toestand === 'belang-gemeld' && uitkomst !== 'belang') {
      ctx.addIssue({ code: 'custom', path: ['belangenverklaring'], message: 'een belang-gemeld verzoek draagt een belang, verklaard of door NWO vastgesteld' })
    }
    if (uitkomst === 'belang' && verzoek.toestand !== 'belang-gemeld') {
      ctx.addIssue({ code: 'custom', path: ['toestand'], message: 'een belang, gemeld of vastgesteld, beëindigt het verzoek' })
    }
    if (verzoek.toestand === 'beantwoord' && uitkomst !== 'geen-belang') {
      ctx.addIssue({ code: 'custom', path: ['toestand'], message: 'een adviseur antwoordt pas na zijn belangenverklaring' })
    }
    if (verzoek.nwoOordelen.length > 0 && verzoek.belangenverklaring === null) {
      ctx.addIssue({ code: 'custom', path: ['nwoOordelen'], message: 'NWO beoordeelt een verklaring die er is' })
    }
    verzoek.nwoOordelen.forEach((oordeel, index) => {
      if (oordeel.persoon === verzoek.adviseur) {
        ctx.addIssue({ code: 'custom', path: ['nwoOordelen', index], message: 'de adviseur beoordeelt zijn eigen verklaring niet' })
      }
      const before = index === 0 ? verzoek.belangenverklaring?.moment : verzoek.nwoOordelen[index - 1]?.moment
      if (before !== undefined && compareMoments(oordeel.moment, before) < 0) {
        ctx.addIssue({ code: 'custom', path: ['nwoOordelen', index], message: 'een oordeel volgt op de verklaring en op het vorige oordeel' })
      }
    })
    const first = verzoek.reacties[0]
    if (first !== undefined && (first.rol !== 'vrager' || first.persoon !== verzoek.vrager)) {
      ctx.addIssue({ code: 'custom', path: ['reacties', 0], message: 'het gesprek begint met de vraag van de vrager' })
    }
    verzoek.reacties.forEach((reactie, index) => {
      if (reactie.rol === 'adviseur' && reactie.persoon !== verzoek.adviseur) {
        ctx.addIssue({ code: 'custom', path: ['reacties', index], message: 'een reactie als adviseur is van de adviseur' })
      }
      const previous = verzoek.reacties[index - 1]
      if (previous !== undefined && compareMoments(reactie.moment, previous.moment) < 0) {
        ctx.addIssue({ code: 'custom', path: ['reacties', index], message: 'de reacties staan oudste eerst' })
      }
    })
    if (compareMoments(verzoek.gewijzigdOp, verzoek.aangemaaktOp) < 0) {
      ctx.addIssue({ code: 'custom', path: ['gewijzigdOp'], message: 'een verzoek verandert niet voordat het bestond' })
    }
  })
export type AdviesVerzoek = z.infer<typeof AdviesVerzoek>

/**
 * Wat er geldt over het belang van de adviseur: het laatste oordeel van NWO, en zonder oordeel zijn
 * eigen verklaring; null zolang hij niet heeft verklaard (R-184d, R-184k).
 */
export function belangUitkomst(
  verzoek: Pick<AdviesVerzoek, 'belangenverklaring'> & { readonly nwoOordelen?: readonly Pick<BelangOordeel, 'uitkomst'>[] },
): 'geen-belang' | 'belang' | null {
  return verzoek.nwoOordelen?.at(-1)?.uitkomst ?? verzoek.belangenverklaring?.uitkomst ?? null
}

/**
 * De gebeurtenissen van een adviesverzoek, elk een eigen bericht aan het processysteem
 * (`koppelvlak.md` hoofdstuk 3).
 *
 * `advies-gevraagd` ook bij een vervolgvraag: de toestand gaat dan terug naar gevraagd, en het bericht
 * draagt het gesprek met de nieuwe vraag. `advies-belang-verklaard` bij elke belangenverklaring; de
 * uitkomst staat in het bericht, en bij een belang is de toestand belang-gemeld.
 * `advies-belang-beoordeeld` bij elk oordeel van NWO over die verklaring (R-184k): het oordeel staat
 * als laatste in `nwoOordelen`, en de toestand zegt of het verzoek loopt of eindigde.
 */
export const AdviesGebeurtenis = z.enum([
  'advies-gevraagd',
  'advies-belang-verklaard',
  'advies-belang-beoordeeld',
  'advies-gegeven',
  'advies-ingetrokken',
  'advies-afgerond',
  'advies-vervallen',
  'advies-termijn-gewijzigd',
  'advies-herinnering-gevraagd',
])
export type AdviesGebeurtenis = z.infer<typeof AdviesGebeurtenis>

/**
 * Het bericht bij een adviesverzoek (`koppelvlak.md` hoofdstuk 3).
 *
 * Geen revisie, net als `uitgenodigd`: geen `revisieId`, geen `publicatieRef`, geen `deadline`. In de
 * inhoud de hele toestand van het verzoek, met de teksten van vraag en antwoord (besluit van 2
 * november 2026: onze opslag is de bron, Dataverse krijgt per volgnummer een kopie). Het e-mailadres
 * van de adviseur staat er niet in; dat reist alleen in `uitgenodigd`. `uitnodigingEventId` wijst naar
 * dat bericht, zodat het processysteem de twee aan elkaar kan knopen.
 */
export const AdviesMessage = z.strictObject({
  eventId: EventId,
  gebeurtenis: AdviesGebeurtenis,
  dossierId: DossierId,
  instantieId: InstantieId,
  moment: Moment,
  advies: AdviesVerzoek,
  uitnodigingEventId: EventId,
  /** Of er een toetssectie bij de gedeelde secties zit: dan leest de adviseur ook de toetsoordelen. */
  toetsoordelenGedeeld: z.boolean(),
})
export type AdviesMessage = z.infer<typeof AdviesMessage>

/**
 * De instelling in de definitie (besluit van 30 oktober 2026, ontwerp 7.4a).
 *
 * Ontbreekt ze, of staat `aan` op false, dan is advies uit: geen permissieve standaard. Ze bevriest
 * met de publicatie (D-36); een noodschakelaar per publicatie in de opslag kan haar alleen uitzetten.
 */
export const AdviesSetting = z.strictObject({
  aan: z.boolean(),
  /** Wie mag vragen: de toetser in de toetslaag, een invuller aan de interne kant, of beide. */
  vragers: z.array(Role).min(1),
  /** Of een adviseur ook buiten NWO mag staan; anders alleen binnen NWO. */
  adviseurBuitenNwo: z.boolean(),
  /** Secties die niet voor advies te delen zijn en geen anker kunnen zijn. */
  uitgeslotenSecties: z.array(SectionId),
})
export type AdviesSetting = z.infer<typeof AdviesSetting>

/**
 * De noodschakelaar per publicatie (besluit van 30 oktober 2026, ontwerp 7.4a).
 *
 * Een pod wordt nooit bijgewerkt, dus een ronde die loopt is met de definitie alleen niet uit te
 * zetten. Functioneel beheer zet advies per publicatie uit in de opslag. Dit kan alleen beperken:
 * staat advies in de definitie uit, dan zet `uit: false` het niet aan. Uit betekent geen nieuwe
 * verzoeken; wat er al is blijft leesbaar en kan worden afgemaakt, en er wordt niets verwijderd.
 */
export const AdviesNoodstop = z.strictObject({
  publicatieId: z.string().min(1),
  uit: z.boolean(),
  /** Wie het laatst schakelde, zoals de configurator hem kent. */
  gewijzigdDoor: z.string().min(1),
  gewijzigdOp: Moment,
})
export type AdviesNoodstop = z.infer<typeof AdviesNoodstop>
